import React, { useMemo, useState } from 'react';
import type { RecommendedSurfaceKey } from '../attentionRouting';
import { RECOMMENDED_SURFACE_LABELS } from '../attentionRouting';
import { isCompanyArtifactInstanceId } from '../detail/resolveArtifactInstanceId';
import { DRIVE_CANONICAL_STATE_ORDER } from './driveCanonicalLanes';
import {
  buildDriveFlowViewModel,
  detailLevelToMode,
  orderedStateEntries,
  type DriveLaneViewModel,
  type DriveTypeStateCounts,
  typeStateTotal,
  driveEmptyLaneCopy,
} from './driveLaneAggregation';
import { deriveDemandStageCountsForDriveLane } from './driveDemandStageProgression';
import { DriveDemandStageFunnel } from './DriveDemandStageFunnel';
import {
  adaptDriveHiFiModelForLiveShape,
  nestOrphanLeavesUnderTypedLanes,
} from './driveLiveShapeAdapter';
import { buildDriveHierarchicalFlowViewModel, enrichDriveSubdomains } from './driveSectionLayout';
import { resolveDriveStallExit } from './driveStallExit';
import { DRIVE_STATE_COLOR_TOKENS } from './driveStateColors';
import {
  artifactsForLane,
  artifactsForSubdomain,
  buildDrillScopedModel,
  lifeToCanonicalState,
} from './driveSubdomainDrillHelpers';
import type { DriveArtifactItem, DriveDetailLevel, DriveHiFiModel } from './types';
import styles from '../ControlBridge.module.css';

/** Same nesting the hierarchical flow uses so demand funnel counts include orphan leaves. */
function adaptModelForDriveBoard(model: DriveHiFiModel): DriveHiFiModel {
  const adapted = adaptDriveHiFiModelForLiveShape(model);
  const { subdomains } = nestOrphanLeavesUnderTypedLanes(adapted.subdomains, adapted.sections);
  return { ...adapted, subdomains };
}

const TOKEN_CAP = 8;

function stateColor(state: string, tokens: Record<string, string>): string {
  return tokens[state] ?? tokens.identified ?? '#B4B2A9';
}

function entropyDotClass(entropy: DriveLaneViewModel['entropy']): string {
  if (entropy === 'active') return styles.driveFlowEntropyActive;
  if (entropy === 'dormant') return styles.driveFlowEntropyDormant;
  return styles.driveFlowEntropyUndefined;
}

function LaneHeader({
  lane,
  onOpenArtifactDetail,
}: {
  lane: DriveLaneViewModel;
  onOpenArtifactDetail?: (artifactInstanceId: string) => void;
}) {
  const laneIsArtifact = isCompanyArtifactInstanceId(lane.id);
  return (
    <div className={styles.driveFlowLaneHeader}>
      {laneIsArtifact && onOpenArtifactDetail ? (
        <button
          type="button"
          className={styles.driveDrillLaneTitleBtn}
          onClick={(event) => {
            event.stopPropagation();
            onOpenArtifactDetail(lane.id);
          }}
        >
          {lane.name}
        </button>
      ) : (
        <span className={styles.driveFlowLaneName}>{lane.name}</span>
      )}
      <span
        className={`${styles.driveFlowEntropyDot} ${entropyDotClass(lane.entropy)}`}
        title={
          lane.entropy === 'active'
            ? 'Entropy computed'
            : lane.entropy === 'dormant'
              ? 'Entropy dormant (8)'
              : 'Entropy undefined (9)'
        }
        aria-hidden
      />
    </div>
  );
}

function StateTokens({
  type,
  tokens,
}: {
  type: DriveTypeStateCounts;
  tokens: Record<string, string>;
}) {
  const entries = orderedStateEntries(type);
  return (
    <span className={styles.driveFlowTokenRow}>
      {entries.map(({ state, count }) => {
        const cap = Math.min(count, TOKEN_CAP);
        const dots = Array.from({ length: cap }, (_, i) => (
          <span
            key={`${state}-${i}`}
            className={styles.driveFlowStateTok}
            style={{ background: stateColor(state, tokens) }}
          />
        ));
        return (
          <React.Fragment key={state}>
            {dots}
            {count > TOKEN_CAP ? (
              <span className={styles.driveFlowTokenOverflow}>+{count - TOKEN_CAP}</span>
            ) : null}
          </React.Fragment>
        );
      })}
    </span>
  );
}

function InteractiveStateTokens({
  items,
  tokens,
  onOpenItemDetail,
}: {
  items: DriveArtifactItem[];
  tokens: Record<string, string>;
  onOpenItemDetail?: (itemId: string) => void;
}) {
  const visible = items.slice(0, TOKEN_CAP);
  const overflow = items.length - visible.length;
  return (
    <span className={styles.driveFlowTokenRow}>
      {visible.map((art) => {
        const canonical = lifeToCanonicalState(art.life);
        if (onOpenItemDetail) {
          return (
            <button
              key={art.id}
              type="button"
              className={styles.driveDrillTokenBtn}
              onClick={(event) => {
                event.stopPropagation();
                onOpenItemDetail(art.id);
              }}
              aria-label={`View item ${art.title}`}
              title={art.title}
            >
              <span
                className={styles.driveFlowStateTok}
                style={{ background: stateColor(canonical, tokens) }}
              />
            </button>
          );
        }
        return (
          <span
            key={art.id}
            className={styles.driveFlowStateTok}
            style={{ background: stateColor(canonical, tokens) }}
          />
        );
      })}
      {overflow > 0 ? <span className={styles.driveFlowTokenOverflow}>+{overflow}</span> : null}
    </span>
  );
}

function SummaryBody({ lane }: { lane: DriveLaneViewModel }) {
  if (lane.types.length === 0) {
    return <div className={styles.driveFlowEmpty}>{driveEmptyLaneCopy(lane.hasFlow)}</div>;
  }
  const parts = lane.types.map((t) => `${typeStateTotal(t)} ${t.typeLabel}`);
  return (
    <div className={styles.driveFlowSummaryBody}>
      {parts.join(' · ')}
      {lane.note ? <span className={styles.driveFlowNote}> · {lane.note}</span> : null}
    </div>
  );
}

function TokenBody({
  lane,
  tokens,
  laneArtifacts,
  onOpenItemDetail,
}: {
  lane: DriveLaneViewModel;
  tokens: Record<string, string>;
  laneArtifacts?: DriveArtifactItem[];
  onOpenItemDetail?: (itemId: string) => void;
}) {
  if (lane.types.length === 0) {
    return <div className={styles.driveFlowEmpty}>{driveEmptyLaneCopy(lane.hasFlow)}</div>;
  }
  return (
    <div className={styles.driveFlowTokenBody}>
      {lane.types.map((t) => (
        <div key={t.typeKey} className={styles.driveFlowTypeRow}>
          <span className={styles.driveFlowTypeName}>{t.typeLabel}</span>
          {laneArtifacts && laneArtifacts.length > 0 ? (
            <InteractiveStateTokens
              items={laneArtifacts}
              tokens={tokens}
              onOpenItemDetail={onOpenItemDetail}
            />
          ) : (
            <StateTokens type={t} tokens={tokens} />
          )}
        </div>
      ))}
    </div>
  );
}

function DetailedBody({
  lane,
  tokens,
  laneArtifacts,
  onOpenItemDetail,
}: {
  lane: DriveLaneViewModel;
  tokens: Record<string, string>;
  laneArtifacts?: DriveArtifactItem[];
  onOpenItemDetail?: (itemId: string) => void;
}) {
  if (lane.types.length === 0) {
    return (
      <div className={styles.driveFlowEmpty}>{driveEmptyLaneCopy(lane.hasFlow)}</div>
    );
  }
  return (
    <>
      <div className={styles.driveFlowStationRow}>
        {lane.types.map((t, index) => (
          <React.Fragment key={t.typeKey}>
            {index > 0 ? <span className={styles.driveFlowArrow} aria-hidden>→</span> : null}
            <div className={styles.driveFlowStation}>
              <div className={styles.driveFlowTypeName}>{t.typeLabel}</div>
              {laneArtifacts && laneArtifacts.length > 0 ? (
                <InteractiveStateTokens
                  items={laneArtifacts}
                  tokens={tokens}
                  onOpenItemDetail={onOpenItemDetail}
                />
              ) : (
                <StateTokens type={t} tokens={tokens} />
              )}
              <div className={styles.driveFlowStationTotal}>{typeStateTotal(t)}</div>
            </div>
          </React.Fragment>
        ))}
      </div>
      {lane.flowHint ? <div className={styles.driveFlowHint}>{lane.flowHint}</div> : null}
      {lane.note ? <div className={styles.driveFlowNoteBlock}>{lane.note}</div> : null}
    </>
  );
}

function DriveExitCtas({
  lane,
  recommendedSurface,
  onNavigate,
}: {
  lane: DriveLaneViewModel;
  recommendedSurface?: RecommendedSurfaceKey;
  onNavigate?: (surface: string) => void;
}) {
  const exit = resolveDriveStallExit(lane, recommendedSurface);
  if (!exit || !onNavigate) return null;

  return (
    <div className={styles.driveFlowExitCtas} data-testid={`drive-exit-ctas-${lane.id}`}>
      <span className={styles.driveFlowExitReason}>{exit.reason}</span>
      <button
        type="button"
        className={styles.driveFlowExitBtn}
        onClick={(event) => {
          event.stopPropagation();
          onNavigate(exit.target);
        }}
      >
        Go to {RECOMMENDED_SURFACE_LABELS[exit.target]}
      </button>
    </div>
  );
}

function LaneCard({
  lane,
  mode,
  tokens,
  model,
  recommendedSurface,
  onNavigate,
  onToggleSubdomain,
  expanded,
  hasNestedLanes,
  laneArtifacts,
  onOpenArtifactDetail,
  onOpenItemDetail,
}: {
  lane: DriveLaneViewModel;
  mode: 'summary' | 'token' | 'detailed';
  tokens: Record<string, string>;
  model: DriveHiFiModel;
  recommendedSurface?: RecommendedSurfaceKey;
  onNavigate?: (surface: string) => void;
  onToggleSubdomain?: (subdomainId: string) => void;
  expanded?: boolean;
  hasNestedLanes?: boolean;
  laneArtifacts?: DriveArtifactItem[];
  onOpenArtifactDetail?: (artifactInstanceId: string) => void;
  onOpenItemDetail?: (itemId: string) => void;
}) {
  const gridStyle =
    mode !== 'detailed' && lane.gridKey !== 'other'
      ? ({ gridArea: lane.gridKey } as React.CSSProperties)
      : undefined;

  const subdomain = model.subdomains.find((sub) => sub.id === lane.id);
  const demandStages = subdomain
    ? deriveDemandStageCountsForDriveLane(
        subdomain,
        model.subdomains,
        model.sections,
        model.artifacts
      )
    : null;

  return (
    <article
      className={`${styles.driveFlowLane} ${mode === 'detailed' ? styles.driveFlowLaneDetailed : ''} ${onToggleSubdomain && hasNestedLanes ? styles.driveFlowLaneDrillable : ''}`}
      style={gridStyle}
      data-testid={`drive-lane-${lane.id}`}
      aria-expanded={hasNestedLanes ? Boolean(expanded) : undefined}
      onClick={
        onToggleSubdomain && hasNestedLanes
          ? (event) => {
              const target = event.target as HTMLElement;
              if (target.closest(`.${styles.driveFlowExitCtas}`)) return;
              if (target.closest('button')) return;
              onToggleSubdomain(lane.id);
            }
          : undefined
      }
    >
      <LaneHeader lane={lane} onOpenArtifactDetail={onOpenArtifactDetail} />
      {demandStages ? (
        <DriveDemandStageFunnel stages={demandStages} compact={mode === 'summary'} />
      ) : null}
      {mode === 'summary' ? <SummaryBody lane={lane} /> : null}
      {mode === 'token' ? (
        <TokenBody
          lane={lane}
          tokens={tokens}
          laneArtifacts={laneArtifacts}
          onOpenItemDetail={onOpenItemDetail}
        />
      ) : null}
      {mode === 'detailed' ? (
        <DetailedBody
          lane={lane}
          tokens={tokens}
          laneArtifacts={laneArtifacts}
          onOpenItemDetail={onOpenItemDetail}
        />
      ) : null}
      {onToggleSubdomain && hasNestedLanes ? (
        <div className={styles.driveFlowDrillHint}>
          {expanded ? 'Hide lanes' : 'Show lanes and steps'}
        </div>
      ) : null}
      <DriveExitCtas
        lane={lane}
        recommendedSurface={recommendedSurface}
        onNavigate={onNavigate}
      />
    </article>
  );
}

function DriveFlowLegend({ tokens }: { tokens: Record<string, string> }) {
  return (
    <div className={styles.driveFlowLegend} aria-label="Drive state colors">
      <span className={styles.driveFlowLegendLabel}>state:</span>
      {DRIVE_CANONICAL_STATE_ORDER.map((state) => (
        <span key={state} className={styles.driveFlowLegendItem}>
          <span
            className={styles.driveFlowStateTok}
            style={{ background: stateColor(state, tokens) }}
          />
          {state}
        </span>
      ))}
      <span className={styles.driveFlowLegendItem}>
        <span className={`${styles.driveFlowEntropyDot} ${styles.driveFlowEntropyActive}`} />
        entropy
      </span>
    </div>
  );
}

type NestedDriveLaneBoard = {
  flow: ReturnType<typeof buildDriveFlowViewModel>;
  model: DriveHiFiModel;
  artifacts: DriveArtifactItem[];
};

function DriveFlowGrid({
  flow,
  mode,
  tokens,
  model,
  recommendedSurface,
  onNavigate,
  expandedSubdomainId,
  onToggleSubdomain,
  nestedBySubdomainId,
  scopedArtifacts,
  scopedSubdomainId,
  stackLanes,
  onOpenArtifactDetail,
  onOpenItemDetail,
}: {
  flow: ReturnType<typeof buildDriveFlowViewModel>;
  mode: 'summary' | 'token' | 'detailed';
  tokens: Record<string, string>;
  model: DriveHiFiModel;
  recommendedSurface?: RecommendedSurfaceKey;
  onNavigate?: (surface: string) => void;
  expandedSubdomainId?: string | null;
  onToggleSubdomain?: (subdomainId: string) => void;
  nestedBySubdomainId?: Map<string, NestedDriveLaneBoard>;
  scopedArtifacts?: DriveArtifactItem[];
  scopedSubdomainId?: string;
  stackLanes?: boolean;
  onOpenArtifactDetail?: (artifactInstanceId: string) => void;
  onOpenItemDetail?: (itemId: string) => void;
}) {
  const interactive = Boolean(scopedArtifacts && onOpenItemDetail);

  const laneArtifactsFor = (lane: DriveLaneViewModel): DriveArtifactItem[] | undefined => {
    if (!interactive || !scopedArtifacts) return undefined;
    return artifactsForLane(lane.id, scopedSubdomainId ?? lane.id, scopedArtifacts);
  };

  const laneCards = flow.lanes.map((lane) => {
    const nested = nestedBySubdomainId?.get(lane.id);
    const hasChildLanes = Boolean(nested && nested.flow.lanes.length > 0);
    const hasNestedLanes = Boolean(onToggleSubdomain) && (hasChildLanes || !scopedSubdomainId);
    const expanded = expandedSubdomainId === lane.id;
    const stepArtifacts =
      nested?.artifacts ??
      artifactsForSubdomain(model.artifacts, lane.id, []);
    return (
      <div key={lane.id} className={styles.driveFlowSubdomainBlock}>
        <LaneCard
          lane={lane}
          mode={mode}
          tokens={tokens}
          model={model}
          recommendedSurface={recommendedSurface}
          onNavigate={onNavigate}
          onToggleSubdomain={onToggleSubdomain}
          expanded={expanded}
          hasNestedLanes={hasNestedLanes}
          laneArtifacts={laneArtifactsFor(lane)}
          onOpenArtifactDetail={onOpenArtifactDetail}
          onOpenItemDetail={onOpenItemDetail}
        />
        {expanded && hasChildLanes && nested ? (
          <div
            className={styles.driveFlowNestedLanes}
            data-testid={`drive-nested-lanes-${lane.id}`}
          >
            <DriveFlowGrid
              flow={nested.flow}
              mode={mode === 'summary' ? 'token' : mode}
              tokens={tokens}
              model={nested.model}
              recommendedSurface={recommendedSurface}
              onNavigate={onNavigate}
              scopedArtifacts={nested.artifacts}
              scopedSubdomainId={lane.id}
              stackLanes
              onOpenArtifactDetail={onOpenArtifactDetail}
              onOpenItemDetail={onOpenItemDetail}
            />
          </div>
        ) : null}
        {expanded && !hasChildLanes && onToggleSubdomain ? (
          <div
            className={styles.driveFlowNestedLanes}
            data-testid={`drive-nested-lanes-${lane.id}`}
          >
            {mode === 'detailed' ? (
              <DetailedBody
                lane={lane}
                tokens={tokens}
                laneArtifacts={stepArtifacts}
                onOpenItemDetail={onOpenItemDetail}
              />
            ) : (
              <TokenBody
                lane={lane}
                tokens={tokens}
                laneArtifacts={stepArtifacts}
                onOpenItemDetail={onOpenItemDetail}
              />
            )}
          </div>
        ) : null}
      </div>
    );
  });

  if (mode === 'detailed' || stackLanes || (nestedBySubdomainId && nestedBySubdomainId.size > 0)) {
    return <div className={styles.driveFlowDetailedStack}>{laneCards}</div>;
  }

  return <div className={styles.driveFlowGrid}>{laneCards}</div>;
}

function DriveFlowSectionBoard({
  flow,
  mode,
  tokens,
  model,
  recommendedSurface,
  onNavigate,
  expandedSubdomainId,
  onToggleSubdomain,
  nestedBySubdomainId,
  onOpenArtifactDetail,
  onOpenItemDetail,
}: {
  flow: ReturnType<typeof buildDriveHierarchicalFlowViewModel>;
  mode: 'summary' | 'token' | 'detailed';
  tokens: Record<string, string>;
  model: DriveHiFiModel;
  recommendedSurface?: RecommendedSurfaceKey;
  onNavigate?: (surface: string) => void;
  expandedSubdomainId?: string | null;
  onToggleSubdomain?: (subdomainId: string) => void;
  nestedBySubdomainId?: Map<string, NestedDriveLaneBoard>;
  onOpenArtifactDetail?: (artifactInstanceId: string) => void;
  onOpenItemDetail?: (itemId: string) => void;
}) {
  const gridProps = {
    mode,
    tokens,
    model,
    recommendedSurface,
    onNavigate,
    expandedSubdomainId,
    onToggleSubdomain,
    nestedBySubdomainId,
    onOpenArtifactDetail,
    onOpenItemDetail,
  };

  if (!flow.hasSections) {
    return <DriveFlowGrid flow={flow} {...gridProps} />;
  }

  return (
    <div className={styles.driveFlowSectionStack} data-testid="drive-section-board">
      {flow.sectionGroups.map((group) => (
        <section
          key={group.section.id}
          className={styles.driveFlowSection}
          data-testid={`drive-section-${group.section.id}`}
        >
          <header className={styles.driveFlowSectionHeader}>
            <h4 className={styles.driveFlowSectionTitle}>{group.section.name}</h4>
          </header>
          <DriveFlowGrid
            flow={{ domainLabel: flow.domainLabel, lanes: group.lanes }}
            {...gridProps}
          />
        </section>
      ))}
    </div>
  );
}

export interface DriveArtifactFlowBoardProps {
  model: DriveHiFiModel;
  lod: DriveDetailLevel;
  recommendedSurface?: RecommendedSurfaceKey;
  onNavigate?: (surface: string) => void;
  onOpenArtifactDetail?: (artifactInstanceId: string) => void;
  onOpenItemDetail?: (itemId: string) => void;
}

export const DriveArtifactFlowBoard: React.FC<DriveArtifactFlowBoardProps> = ({
  model,
  lod,
  recommendedSurface,
  onNavigate,
  onOpenArtifactDetail,
  onOpenItemDetail,
}) => {
  const [expandedSubdomainId, setExpandedSubdomainId] = useState<string | null>(null);
  const boardModel = useMemo(() => adaptModelForDriveBoard(model), [model]);
  const flow = buildDriveHierarchicalFlowViewModel(boardModel);
  const mode = detailLevelToMode(lod);
  const tokens = boardModel.stateColorTokens ?? DRIVE_STATE_COLOR_TOKENS;

  const enrichedSubdomains = useMemo(
    () => enrichDriveSubdomains(boardModel.subdomains),
    [boardModel.subdomains]
  );

  const nestedBySubdomainId = useMemo(() => {
    const nested = new Map<string, NestedDriveLaneBoard>();
    for (const sub of enrichedSubdomains) {
      const grands = sub.grandchildren ?? [];
      if (grands.length === 0) continue;
      const artifacts = artifactsForSubdomain(
        boardModel.artifacts,
        sub.id,
        grands.map((g) => g.id)
      );
      const scoped = buildDrillScopedModel(sub, artifacts, sub.name);
      nested.set(sub.id, {
        flow: buildDriveFlowViewModel(scoped),
        model: scoped,
        artifacts,
      });
    }
    return nested;
  }, [boardModel.artifacts, enrichedSubdomains]);

  return (
    <div className={styles.driveFlowRoot} data-testid="drive-artifact-flow-board" data-mode={mode}>
      <div className={styles.driveFlowHeading}>
        <div>
          <div className={styles.driveFlowTitle}>Drive</div>
          <div className={styles.driveFlowSubtitle}>
            {flow.domainLabel} · subdomains expand into flow lanes, then steps in sequence
          </div>
        </div>
      </div>

      <DriveFlowSectionBoard
        flow={flow}
        mode={mode}
        tokens={tokens}
        model={boardModel}
        recommendedSurface={recommendedSurface}
        onNavigate={onNavigate}
        expandedSubdomainId={expandedSubdomainId}
        onToggleSubdomain={(id) =>
          setExpandedSubdomainId((prev) => (prev === id ? null : id))
        }
        nestedBySubdomainId={nestedBySubdomainId}
        onOpenArtifactDetail={onOpenArtifactDetail}
        onOpenItemDetail={onOpenItemDetail}
      />

      <DriveFlowLegend tokens={tokens} />
    </div>
  );
};
